import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { SPINNER_ICON, STAR_ICON, STAR_ICON_FILL } from 'app/constants';
import { head, eq, find } from 'lodash';
import { OutletProfile, Notification } from 'app/models';
import {
  addOutletToProject,
  deleteOutletToProject,
  updateOutletLabel,
  initAuthorOutletToProjectState,
  resetOutletProfile,
  resetSearch,
  showNotification,
  tabControl,
  unSetSearch,
} from 'app/redux/actions';
import { AuthenticationService, OutletService } from 'app/services';

@Component({
  selector: 'app-outlet-contact-profile',
  templateUrl: './outlet-contact-profile.component.html',
  styles: [
    `
      :host .p-button.p-button-icon-only {
        width: 20px !important;
        border-color: transparent;
      }

      :host .p-button.p-button-icon-only .p-button-icon {
        font-size: 0.9rem;
      }

      :host .p-button:focus {
        box-shadow: none !important;
      }

      :host .p-button:hover {
        background: none !important;
      }

      .read-more-link {
        color: #56717d;
        cursor: pointer;
        text-decoration: none;
        font-weight: 700;
      }

      .read-more-link:hover {
        text-decoration: underline;
      }
    `,
  ],
})
export class OutletProfileComponent implements OnInit {
  public actionType: string;
  public isOutletSaved: boolean;
  public project: any;
  public starToggle: string;
  public outlet: OutletProfile;
  public outletsOutletsList: any[];
  public picture: string;
  public isAddedingOrIsRemoving: boolean;
  public showOutletProfile: boolean;
  public savedOutlet: any;
  public categories: any[];
  public readonly shortcutLabels = ['Primary', 'Secondary'];

  constructor(
    private store: Store<AppState>,
    private outletService: OutletService,
    private auth: AuthenticationService
  ) {
    this.outlet = null;
    this.isOutletSaved = false;
    this.actionType = null;
    this.project = null;
    this.starToggle = STAR_ICON;
    this.outletsOutletsList = [];
    this.isAddedingOrIsRemoving = false;
    this.showOutletProfile = true;
    this.savedOutlet = null;
    this.categories = [];
  }

  ngOnInit() {
    this.store.select('search').subscribe(({ searchBody }) => {
      this.showOutletProfile = searchBody?.outlets?.length < 2;
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.project = projectSelected;
      }
    });

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    });

    this.store
      .select('outletContactDetails')
      .subscribe(({ outletContactProfile }) => {
        this.outlet = null;
        if (outletContactProfile) {
          this.outlet = outletContactProfile;
          this.picture = this.outletService.getOutletPicture(
            outletContactProfile.id
          );
          this.setOutletSaved();
        }
      });

    this.store
      .select('authorsOutletsList')
      .subscribe(
        ({
          outlets,
          isAddingAuthorOutlet,
          isDeletingAuthorOutlet,
          hasAuthorOutletDeleted,
          hasAuthorOutletAdded,
          errors,
        }) => {
          this.outletsOutletsList = outlets;

          if (isAddingAuthorOutlet || isDeletingAuthorOutlet) {
            this.starToggle = SPINNER_ICON;
          }

          if (hasAuthorOutletAdded) {
            this.setOutletSaved();
          }

          if (hasAuthorOutletDeleted) {
            this.setOutletSaved();
          }

          if (errors?.message) {
            this.store.dispatch(
              showNotification({
                notify: new Notification(
                  `Error ${this.actionType} outlet to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setOutletSaved();
            setTimeout(() => {
              this.store.dispatch(initAuthorOutletToProjectState());
            }, 500);
          }
        }
      );
  }

  handleSaveOutlet(outlet) {
    if (!this.isAddedingOrIsRemoving) {
      this.isAddedingOrIsRemoving = true;
      this.starToggle = SPINNER_ICON;
      if (this.isOutletSaved) {
        this.actionType = 'deleting';
        this.store.dispatch(
          deleteOutletToProject({
            partyId: outlet.id,
            userProject: this.project,
            outlet,
          })
        );
      } else {
        this.actionType = 'adding';
        this.store.dispatch(
          addOutletToProject({
            partyId: outlet.id,
            userProject: this.project,
            outlet,
          })
        );
      }
    }
  }

  handleIgnoreOutlet(currentlyIgnored: boolean) {
    if (currentlyIgnored) {
      this.handleSaveOutlet(this.outlet);
      return;
    }
    const ignore = this.findCategory('Ignore');
    if (!ignore) {
      this.showLabelError('Ignore');
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(
      addOutletToProject({
        partyId: this.outlet.id,
        userProject: this.project,
        outlet: this.outlet,
        userLabelId: ignore.id,
      })
    );
  }

  handleOutletLabel(labelName: string) {
    const category = this.findCategory(labelName);
    if (!category || !this.savedOutlet) {
      this.showLabelError(labelName);
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(
      updateOutletLabel({
        userPartyId: this.savedOutlet.id,
        userProject: this.project,
        outlet: this.outlet,
        userLabelId: this.outletLabel === labelName ? null : category.id,
      })
    );
  }

  get isOutletIgnored(): boolean {
    return this.outletLabel === 'Ignore';
  }

  get outletLabel(): string {
    return (
      this.savedOutlet?.categoryName || this.savedOutlet?.label?.name || null
    );
  }

  handleBackToHome() {
    this.store.dispatch(resetOutletProfile());
    this.store.dispatch(tabControl({ index: 0 }));
    this.store.dispatch(unSetSearch());
    this.store.dispatch(resetSearch({ onReset: true }));
  }

  private setOutletSaved() {
    setTimeout(() => {
      this.savedOutlet = find(this.outletsOutletsList, (i) =>
        eq(i.partyId, this.outlet.id)
      );
      this.isOutletSaved = !!this.savedOutlet;
      this.starToggle = this.isOutletSaved ? STAR_ICON_FILL : STAR_ICON;
      this.isAddedingOrIsRemoving = false;
    }, 500);
  }

  private findCategory(name: string): any {
    return find(
      this.categories,
      (category) => String(category.name).toLowerCase() === name.toLowerCase()
    );
  }

  private showLabelError(labelName: string): void {
    this.store.dispatch(
      showNotification({
        notify: new Notification(
          `The ${labelName} label is not available`,
          'error',
          'tl'
        ),
      })
    );
  }

  hasReportingAccess() {
    return this.auth.hasReportingAccess();
  }
}
